import {Component, DestroyRef, HostListener, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {TableModule} from 'primeng/table';
import {CodeStatus, UserCode} from '../../models/user-codes.model';
import {MessageService} from 'primeng/api';
import {AuctionService} from '../../services/auction.service';
import {Router} from '@angular/router';
import {ToastModule} from 'primeng/toast';
import {catchError, filter, interval, of, startWith, switchMap, tap, throwError} from 'rxjs';
import {AuthService} from '../../services/auth.service';
import {PriceFormatPipe} from '../../helpers/pipes/price-format';
import {UserCodeTimerComponent} from '../../components/user-code-timer/user-code-timer.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { NgStyle } from '@angular/common';

@Component({
  selector: 'app-user-codes-page',
  imports: [
    TableModule,
    ToastModule,
    PriceFormatPipe,
    UserCodeTimerComponent,
    NgStyle
],
  templateUrl: './user-codes-page.component.html',
  styleUrl: './user-codes-page.component.scss'
})
export class UserCodesPageComponent implements OnInit {
  @HostListener('document:pointerup')
  onDocPointerUp() { this.stopHold(); }

  @HostListener('document:visibilitychange')
  onVisChange() { if (document.hidden) this.stopHold(); }

  public userCodes: WritableSignal<UserCode[]> = signal<UserCode[]>([]);
  protected readonly CodeStatus: typeof CodeStatus = CodeStatus;
  private auctionService = inject(AuctionService);
  private auth = inject(AuthService);
  private retriedAfterLogin = false;
  private initialFlipPrice = new WeakMap<UserCode, number>();
  private holdTimeoutId: any = null;
  private holdIntervalId: any = null;
  private holdActive = false;
  private suppressNextClick = false;
  private holdTarget?: { code: UserCode; dir: 'inc' | 'dec' };
   errorMessage: string = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(private router: Router,
              private messages: MessageService) {
  }

  public ngOnInit(): void {
    const nav = this.router.getCurrentNavigation();
    const toast = nav?.extras?.state?.['toast'];
    if (toast) this.messages.add(toast);

    interval(3000).pipe(
      startWith(0),
      tap(() => this.fetchCodes()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  public publish(code: UserCode): void {
    this.auctionService.publishCode(code.id).subscribe(() => this.fetchCodes());
  }

  public withdraw(code: UserCode): void {
    this.auctionService.withdrawCode(code.id).subscribe(() => this.fetchCodes());
  }

  public onPlusClick(code: UserCode, ev: Event) {
    if (this.suppressNextClick) { this.suppressNextClick = false; return; }
    this.incrementFlipPrice(code);
  }

  public onMinusClick(code: UserCode, ev: Event) {
    if (this.suppressNextClick) { this.suppressNextClick = false; return; }
    this.decrementFlipPrice(code);
  }

  public absDiff(code: UserCode): number { return Math.abs(this.getDiff(code)); }

  public startHold(code: UserCode, dir: 'inc' | 'dec', ev?: Event) {
    ev?.preventDefault();
    this.stopHold();
    this.holdActive = true;
    this.holdTarget = { code, dir };
    this.suppressNextClick = false;

    this.holdTimeoutId = setTimeout(() => {
      if (!this.holdActive || !this.holdTarget) return;
      this.suppressNextClick = true;


      this.holdIntervalId = setInterval(() => {
        const t = this.holdTarget!;
        if (t.dir === 'inc') {
          this.incrementFlipPrice(t.code);
        } else {
          this.decrementFlipPrice(t.code);
        }
      }, 80);
    }, 300);
  }

  public stopHold() {
    this.holdActive = false;
    if (this.holdTimeoutId) { clearTimeout(this.holdTimeoutId); this.holdTimeoutId = null; }
    if (this.holdIntervalId) { clearInterval(this.holdIntervalId); this.holdIntervalId = null; }
    this.holdTarget = undefined;
  }

  private getDiff(code: UserCode): number {
    this.ensureInitial(code);
    const start = this.initialFlipPrice.get(code)!;
    return code.flipPrice - start;
  }

  private toInt(v: number | null | undefined): number {
    return Math.round(Number(v ?? 0));
  }

  private ensureInitial(code: UserCode): void {
    if (!this.initialFlipPrice.has(code)) {
      this.initialFlipPrice.set(code, code.price);
    }
  }

  private clamp(code: UserCode, value: number): number {
    this.ensureInitial(code);
    const startMin = this.initialFlipPrice.get(code)!;
    const strictMin = code.minPrice != null ? this.toInt(code.minPrice) : startMin;

    return Math.max(strictMin, value);
  }

  private fetchCodes() {
    this.auctionService.getUserCodes().pipe(
      catchError(err => {
        if (( err?.status === 403) && !this.retriedAfterLogin) {
          return this.auth.openLoginDialog$().pipe(
            filter(isLogged => isLogged),
            tap(() => {
              this.retriedAfterLogin = true;
              this.errorMessage = ''
            }),
            switchMap(() => this.auctionService.getUserCodes())
          );
        } 
        else if( err?.status === 401 ){
          this.errorMessage = 'Musisz być zalogowany by zobaczyć swoje kody' 
          console.log("")
        }
        else{
          this.errorMessage = err?.error?.error || 'Nie udało się pobrać kodów.'; 
        const msg = err?.error?.error || 'Nie udało się pobrać kodów.';
        this.messages.add({ severity: 'error', summary: 'Błąd', detail: msg });
        }
        return throwError(() => err);
      }),
      tap(list => {
        this.userCodes.set(list);
      }),
      catchError(err => {
        console.log("err",err)
        // const msg = err?.error?.error || 'Nie udało się pobrać kodów.';
        // this.messages.add({ severity: 'error', summary: 'Błąd', detail: msg });
        return of([]);
      }),
    ).subscribe();
  }

  private incrementFlipPrice(code: UserCode): void {
    this.ensureInitial(code);
    const next = (this.toInt(code.flipPrice) + 1);
    code.flipPrice = this.clamp(code, next);
    this.auctionService.changeCodePrice(code.id, code.flipPrice).subscribe();
  }

  private canDecrement(code: UserCode): boolean {
    this.ensureInitial(code);
    const current = this.toInt(code.flipPrice);
    const min = Math.max(this.initialFlipPrice.get(code)!, this.toInt(code.minPrice ?? 0));
    return (current - 1) >= min;
  }

  private decrementFlipPrice(code: UserCode): void {
    if (!this.canDecrement(code)) return;
    const next = (this.toInt(code.flipPrice) - 1);
    code.flipPrice = this.clamp(code, next);
    this.auctionService.changeCodePrice(code.id, code.flipPrice).subscribe();
  }


}
